import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  useDisclosure,
  VStack,
  Image,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react'
import { FileIcon, X, ZoomIn } from 'lucide-react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'
import { useAuthStore } from '@/store/auth'

export type TicketRequestFormProps = {
  ticketRequestId?: number
  onSuccess?: () => void
  onCancel?: () => void
  /** API path for ticket request CRUD (e.g. 'ticket-management/all-tickets' or 'support/my-request') */
  basePath?: string
  /** When true, hide Severity, Related to, and Assign to (for requestor role) */
  requestorMode?: boolean
}

const DEFAULT_BASE_PATH = 'ticket-management/all-tickets'

type ServiceTypeOption = { id: number; name: string; code: string; parent_id: number | null; approval?: boolean }

type SavedAttachmentMeta = { name: string; size?: number; type?: string; file_url?: string | null; thumbnail_url?: string | null; media_id?: number | null }
type AttachmentPreview = {
  file: File
  preview?: string
  /** True when loaded from server (no real File); use savedMeta for payload */
  isExisting?: boolean
  savedMeta?: SavedAttachmentMeta
}

type TicketSearchItem = { id: number; request_number: string }

type UserSearchItem = {
  id: number
  user_login: string
  user_email?: string
  first_name?: string | null
  last_name?: string | null
}

const initialValues = {
  request_number: '',
  service_type_id: '' as string | number,
  type_of_service_id: '' as string | number,
  description: '',
  contact_number: '',
  contact_name: '',
  contact_email: '',
  slas_id: '' as string | number,
}

export function TicketRequestForm({ ticketRequestId, onSuccess, onCancel, basePath = DEFAULT_BASE_PATH, requestorMode = false }: TicketRequestFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!ticketRequestId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)
  const [allServiceTypes, setAllServiceTypes] = useState<ServiceTypeOption[]>([])
  const [slas, setSlas] = useState<{ id: number; severity: string }[]>([])
  const [attachments, setAttachments] = useState<AttachmentPreview[]>([])
  const [zoomAttachment, setZoomAttachment] = useState<AttachmentPreview | null>(null)
  const [parentTicketId, setParentTicketId] = useState<number | null>(null)
  const [relatedToDisplay, setRelatedToDisplay] = useState<string>('')
  const [relatedSearchQuery, setRelatedSearchQuery] = useState('')
  const [relatedSearchResults, setRelatedSearchResults] = useState<TicketSearchItem[]>([])
  const [relatedSearching, setRelatedSearching] = useState(false)
  const relatedSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [assignedToId, setAssignedToId] = useState<number | null>(null)
  const [assignToDisplay, setAssignToDisplay] = useState<string>('')
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [userSearchResults, setUserSearchResults] = useState<UserSearchItem[]>([])
  const [userSearching, setUserSearching] = useState(false)
  const userSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [assignToMe, setAssignToMe] = useState(false)
  const currentUser = useAuthStore((s) => s.user)
  const { isOpen: isZoomOpen, onOpen: onZoomOpen, onClose: onZoomClose } = useDisclosure()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const serviceTypeParents = useMemo(
    () => allServiceTypes.filter((st) => st.parent_id == null),
    [allServiceTypes]
  )

  const serviceTypeChildren = useMemo(() => {
    const pid = form.service_type_id === '' ? null : Number(form.service_type_id)
    if (pid == null) return []
    return allServiceTypes.filter((st) => st.parent_id === pid)
  }, [allServiceTypes, form.service_type_id])

  const showTypeOfService = serviceTypeChildren.length > 0

  const loadOptions = useCallback(async () => {
    try {
      const [typesRes, slaRes] = await Promise.all([
        apiClient.get('/service-catalog/service-types', { params: { per_page: 200 } }),
        apiClient.get('/service-catalog/sla-timing', { params: { per_page: 200 } }),
      ])
      const typesData = typesRes.data?.data ?? typesRes.data
      const slaData = slaRes.data?.data ?? slaRes.data
      const list = Array.isArray(typesData) ? typesData : typesData?.data ?? []
      setAllServiceTypes(list)
      setSlas(Array.isArray(slaData) ? slaData : [])
    } catch (e) {
      console.error('Failed to load options', e)
    }
  }, [])

  const loadOne = useCallback(async () => {
    if (!ticketRequestId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`${basePath}/${ticketRequestId}`)
      const r = data?.data ?? data
      const serviceTypeId = r.service_type_id ?? ''
      const st = allServiceTypes.find((s) => s.id === serviceTypeId)
      const isChild = st?.parent_id != null
      setForm({
        request_number: r.request_number ?? '',
        service_type_id: isChild ? (st?.parent_id ?? '') : serviceTypeId,
        type_of_service_id: isChild ? serviceTypeId : '',
        description: r.description ?? '',
        contact_number: r.contact_number ?? '',
        contact_name: r.contact_name ?? '',
        contact_email: r.contact_email ?? '',
        slas_id: r.slas_id ?? '',
      })
      const parentId = r.parent_ticket_id ?? null
      setParentTicketId(parentId)
      if (parentId && parentId !== ticketRequestId) {
        try {
          const parentRes = await apiClient.get(`${basePath}/${parentId}`)
          const parentData = parentRes.data?.data ?? parentRes.data
          setRelatedToDisplay(parentData?.request_number ?? '')
        } catch {
          setRelatedToDisplay('')
        }
      } else {
        setRelatedToDisplay('')
      }
      if (r.assigned_to) {
        setAssignedToId(r.assigned_to)
        try {
          const userRes = await apiClient.get(`/user-management/users/${r.assigned_to}`)
          const u = userRes.data?.data ?? userRes.data
          setAssignToDisplay(userLabel(u) ?? '')
        } catch {
          setAssignToDisplay('')
        }
        setAssignToMe(r.assigned_to === currentUser?.id)
      } else {
        setAssignedToId(null)
        setAssignToDisplay('')
        setAssignToMe(false)
      }
      const meta = r.attachment_metadata
      if (Array.isArray(meta) && meta.length > 0) {
        setAttachments(
          meta.map((m: SavedAttachmentMeta & { file_name?: string }) => ({
            file: new File([], m?.name ?? m?.file_name ?? 'file'),
            preview: undefined,
            isExisting: true,
            savedMeta: {
              name: m?.name ?? m?.file_name ?? 'file',
              size: m?.size,
              type: m?.type,
              file_url: m?.file_url ?? null,
              thumbnail_url: m?.thumbnail_url ?? null,
              media_id: m?.media_id ?? null,
            },
          }))
        )
      } else {
        setAttachments([])
      }
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [ticketRequestId, showError, allServiceTypes, currentUser?.id])

  useEffect(() => {
    loadOptions()
  }, [loadOptions])

  useEffect(() => {
    if (!ticketRequestId) {
      setForm(initialValues)
      setAttachments([])
      setParentTicketId(null)
      setRelatedToDisplay('')
      setRelatedSearchResults([])
      setAssignedToId(null)
      setAssignToDisplay('')
      setAssignToMe(false)
      setUserSearchResults([])
      return
    }
    if (allServiceTypes.length === 0) return
    loadOne()
  }, [ticketRequestId, allServiceTypes.length, loadOne])

  const searchRelatedTickets = useCallback(async (query: string) => {
    if (!query.trim()) {
      setRelatedSearchResults([])
      return
    }
    setRelatedSearching(true)
    try {
      const { data } = await apiClient.get(basePath, { params: { search: query.trim(), per_page: 10 } })
      const list = data?.data ?? data
      const items = Array.isArray(list) ? list : list?.data ?? []
      const filtered = items.filter((t: TicketSearchItem) => t.id !== ticketRequestId)
      setRelatedSearchResults(filtered)
    } catch {
      setRelatedSearchResults([])
    } finally {
      setRelatedSearching(false)
    }
  }, [ticketRequestId])

  useEffect(() => {
    if (relatedSearchTimeoutRef.current) clearTimeout(relatedSearchTimeoutRef.current)
    if (!relatedSearchQuery.trim()) {
      setRelatedSearchResults([])
      return
    }
    relatedSearchTimeoutRef.current = setTimeout(() => {
      searchRelatedTickets(relatedSearchQuery)
    }, 300)
    return () => {
      if (relatedSearchTimeoutRef.current) clearTimeout(relatedSearchTimeoutRef.current)
    }
  }, [relatedSearchQuery, searchRelatedTickets])

  const selectRelatedTicket = (ticket: TicketSearchItem) => {
    setParentTicketId(ticket.id)
    setRelatedToDisplay(ticket.request_number)
    setRelatedSearchQuery('')
    setRelatedSearchResults([])
  }

  const clearRelatedTicket = () => {
    setParentTicketId(null)
    setRelatedToDisplay('')
    setRelatedSearchQuery('')
    setRelatedSearchResults([])
  }

  const userLabel = (u: UserSearchItem | { user_login?: string; user_email?: string; first_name?: string | null; last_name?: string | null } | null) => {
    if (!u) return ''
    const first = (u as UserSearchItem).first_name ?? ''
    const last = (u as UserSearchItem).last_name ?? ''
    const name = [first, last].filter(Boolean).join(' ').trim()
    return name || (u as UserSearchItem).user_login || (u as any).user_email || ''
  }

  const searchUsers = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setUserSearchResults([])
      return
    }
    setUserSearching(true)
    try {
      const { data } = await apiClient.get('/user-management/users', { params: { search: query.trim(), per_page: 10 } })
      const list = data?.data ?? data
      const items = Array.isArray(list) ? list : list?.data ?? []
      setUserSearchResults(items)
    } catch {
      setUserSearchResults([])
    } finally {
      setUserSearching(false)
    }
  }, [])

  useEffect(() => {
    if (userSearchTimeoutRef.current) clearTimeout(userSearchTimeoutRef.current)
    if (!userSearchQuery.trim() || userSearchQuery.trim().length < 2) {
      setUserSearchResults([])
      return
    }
    userSearchTimeoutRef.current = setTimeout(() => {
      searchUsers(userSearchQuery)
    }, 300)
    return () => {
      if (userSearchTimeoutRef.current) clearTimeout(userSearchTimeoutRef.current)
    }
  }, [userSearchQuery, searchUsers])

  const selectAssignUser = (user: UserSearchItem) => {
    setAssignedToId(user.id)
    setAssignToDisplay(userLabel(user))
    setUserSearchQuery('')
    setUserSearchResults([])
    setAssignToMe(false)
  }

  const clearAssignTo = () => {
    setAssignedToId(null)
    setAssignToDisplay('')
    setUserSearchQuery('')
    setUserSearchResults([])
    setAssignToMe(false)
  }

  const handleAssignToMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    setAssignToMe(checked)
    if (checked && currentUser) {
      setAssignedToId(currentUser.id)
      setAssignToDisplay(userLabel({ id: currentUser.id, user_login: currentUser.user_login, user_email: currentUser.email, first_name: (currentUser as any).first_name, last_name: (currentUser as any).last_name }))
      setUserSearchQuery('')
      setUserSearchResults([])
    } else if (!checked) {
      setAssignedToId(null)
      setAssignToDisplay('')
    }
  }

  const updateField = (field: keyof typeof form, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (field === 'service_type_id') setForm((prev) => ({ ...prev, type_of_service_id: '' }))
    if (errors[field]) setErrors((prev) => { const next = { ...prev }; delete next[field]; return next })
  }

  const effectiveServiceTypeId = useMemo(() => {
    if (showTypeOfService && form.type_of_service_id !== '') return Number(form.type_of_service_id)
    return form.service_type_id === '' ? null : Number(form.service_type_id)
  }, [showTypeOfService, form.type_of_service_id, form.service_type_id])

  const effectiveServiceType = useMemo(() => {
    if (effectiveServiceTypeId == null) return null
    return allServiceTypes.find((s) => s.id === effectiveServiceTypeId) ?? null
  }, [effectiveServiceTypeId, allServiceTypes])

  const isImage = (f: File) => f.type.startsWith('image/')

  const onFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files?.length) return
    const next: AttachmentPreview[] = []
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const preview = isImage(file) ? URL.createObjectURL(file) : undefined
      next.push({ file, preview })
    }
    setAttachments((prev) => [...prev, ...next])
    e.target.value = ''
  }

  const removeAttachment = (index: number) => {
    setAttachments((prev) => {
      const item = prev[index]
      if (item.preview?.startsWith('blob:')) URL.revokeObjectURL(item.preview)
      return prev.filter((_, i) => i !== index)
    })
  }

  const openZoom = (item: AttachmentPreview) => {
    setZoomAttachment(item)
    onZoomOpen()
  }

  const closeZoom = () => {
    setZoomAttachment(null)
    onZoomClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const existingMeta = attachments
        .filter((a) => a.isExisting && a.savedMeta)
        .map((a) => ({
          name: a.savedMeta!.name,
          size: a.savedMeta!.size ?? 0,
          type: a.savedMeta!.type ?? '',
          file_url: a.savedMeta!.file_url ?? null,
          thumbnail_url: a.savedMeta!.thumbnail_url ?? null,
          media_id: a.savedMeta!.media_id ?? null,
        }))
      const newFiles = attachments.filter((a) => !a.isExisting)
      let attachment_metadata: Array<{ name: string; size?: number; type?: string; file_url?: string | null; thumbnail_url?: string | null; media_id?: number | null }> = [...existingMeta]

      if (newFiles.length > 0) {
        const fd = new FormData()
        newFiles.forEach((a) => fd.append('attachments[]', a.file))
        const uploadRes = await apiClient.post(`${basePath}/upload-attachments`, fd, { headers: { 'Content-Type': undefined } })
        const uploaded = (uploadRes.data?.attachment_metadata ?? uploadRes.data ?? []) as typeof attachment_metadata
        if (Array.isArray(uploaded) && uploaded.length > 0) {
          const normalized = uploaded.map((u) => ({
            name: u.name,
            size: u.size ?? 0,
            type: u.type ?? '',
            file_url: u.file_url ?? null,
            thumbnail_url: u.thumbnail_url ?? null,
            media_id: u.media_id ?? null,
          }))
          attachment_metadata = existingMeta.concat(normalized)
        }
      }

      const payload = {
        parent_ticket_id: requestorMode ? null : parentTicketId,
        service_type_id: effectiveServiceTypeId,
        description: form.description.trim() || null,
        contact_number: form.contact_number.trim() || null,
        contact_name: form.contact_name.trim() || null,
        contact_email: form.contact_email.trim() || null,
        slas_id: requestorMode ? null : (form.slas_id === '' ? null : Number(form.slas_id)),
        for_approval: effectiveServiceType?.approval ? 1 : 2,
        assigned_to: requestorMode ? null : (assignToMe ? (currentUser?.id ?? null) : assignedToId),
        attachment_metadata: attachment_metadata.length > 0 ? attachment_metadata : null,
      }

      if (ticketRequestId) {
        await apiClient.put(`${basePath}/${ticketRequestId}`, payload)
        showSuccess('Success', 'Ticket updated successfully')
      } else {
        await apiClient.post(basePath, payload)
        showSuccess('Success', 'Ticket created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') setErrors(res.errors)
      else showError(err as Error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Box py={8}>Loading...</Box>

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        {ticketRequestId && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Request #</FormLabel>
            <FormControl>
              <Input value={form.request_number} isReadOnly bg="gray.50" />
            </FormControl>
          </Grid>
        )}

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Service type</FormLabel>
          <FormControl isInvalid={!!errors.service_type_id?.length}>
            <Select
              value={form.service_type_id === '' ? '' : form.service_type_id}
              onChange={(e) => updateField('service_type_id', e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="Select service type"
            >
              {serviceTypeParents.map((st) => (
                <option key={st.id} value={st.id}>{st.name}</option>
              ))}
            </Select>
            <FormErrorMessage>{errors.service_type_id?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        {showTypeOfService && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Type of Service</FormLabel>
            <FormControl isInvalid={!!errors.type_of_service_id?.length}>
              <Select
                value={form.type_of_service_id === '' ? '' : form.type_of_service_id}
                onChange={(e) => updateField('type_of_service_id', e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Select type of service"
              >
                {serviceTypeChildren.map((st) => (
                  <option key={st.id} value={st.id}>{st.name}</option>
                ))}
              </Select>
              <FormErrorMessage>{errors.type_of_service_id?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>
        )}

        {!requestorMode && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Severity</FormLabel>
            <FormControl isInvalid={!!errors.slas_id?.length}>
              <Select
                value={form.slas_id === '' ? '' : form.slas_id}
                onChange={(e) => updateField('slas_id', e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Select severity"
              >
                {slas.map((s) => (
                  <option key={s.id} value={s.id}>Severity {s.severity ?? s.id}</option>
                ))}
              </Select>
              <FormErrorMessage>{errors.slas_id?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>
        )}

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Description</FormLabel>
          <FormControl isInvalid={!!errors.description?.length}>
            <Textarea value={form.description} onChange={(e) => updateField('description', e.target.value)} placeholder="Request description" rows={3} />
            <FormErrorMessage>{errors.description?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Attachment</FormLabel>
          <FormControl>
            <Input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx"
              onChange={onFilesSelected}
              display="none"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              Add files
            </Button>
            {attachments.length > 0 && (
              <SimpleGrid columns={{ base: 2, sm: 4, md: 5 }} spacing={3} mt={3}>
                {attachments.map((item, index) => {
                  const imageUrl = item.preview?.startsWith('blob:') ? item.preview : (item.isExisting && item.savedMeta?.file_url && item.savedMeta?.type?.startsWith('image/') ? item.savedMeta.file_url : null)
                  return (
                  <Box key={index} position="relative" boxShadow="md" borderRadius="md" overflow="hidden" bg="gray.100">
                    {imageUrl ? (
                      <Box
                        as="button"
                        type="button"
                        onClick={() => openZoom(item)}
                        cursor="pointer"
                        display="block"
                        w="full"
                        h="24"
                        position="relative"
                      >
                        <Image src={imageUrl} alt={item.savedMeta?.name ?? item.file.name} objectFit="cover" w="full" h="full" />
                        <Box position="absolute" inset={0} bg="blackAlpha.400" opacity={0} _hover={{ opacity: 1 }} transition="opacity 0.2s" display="flex" alignItems="center" justifyContent="center">
                          <ZoomIn color="white" size={24} />
                        </Box>
                      </Box>
                    ) : (
                      <Box w="full" h="24" display="flex" alignItems="center" justifyContent="center" bg="gray.200">
                        <FileIcon size={28} />
                      </Box>
                    )}
                    <Text noOfLines={2} fontSize="xs" px={2} py={1} title={item.savedMeta?.name ?? item.file.name}>{item.savedMeta?.name ?? item.file.name}</Text>
                    <IconButton
                      aria-label="Remove"
                      icon={<X size={14} />}
                      size="xs"
                      position="absolute"
                      top={1}
                      right={1}
                      colorScheme="red"
                      onClick={() => removeAttachment(index)}
                    />
                  </Box>
                  )
                })}
              </SimpleGrid>
            )}
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Contact name</FormLabel>
          <FormControl isInvalid={!!errors.contact_name?.length}>
            <Input value={form.contact_name} onChange={(e) => updateField('contact_name', e.target.value)} placeholder="Contact name" />
            <FormErrorMessage>{errors.contact_name?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Contact email</FormLabel>
          <FormControl isInvalid={!!errors.contact_email?.length}>
            <Input type="email" value={form.contact_email} onChange={(e) => updateField('contact_email', e.target.value)} placeholder="Contact email" />
            <FormErrorMessage>{errors.contact_email?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Contact number</FormLabel>
          <FormControl isInvalid={!!errors.contact_number?.length}>
            <Input value={form.contact_number} onChange={(e) => updateField('contact_number', e.target.value)} placeholder="Contact number" />
            <FormErrorMessage>{errors.contact_number?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        {!requestorMode && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Related to</FormLabel>
            <FormControl isInvalid={!!errors.parent_ticket_id?.length}>
              <Box position="relative">
                {relatedToDisplay ? (
                  <HStack>
                    <Input value={relatedToDisplay} isReadOnly bg="gray.50" />
                    <Button type="button" size="sm" variant="outline" onClick={clearRelatedTicket}>
                      Clear
                    </Button>
                  </HStack>
                ) : (
                  <>
                    <Input
                      value={relatedSearchQuery}
                      onChange={(e) => setRelatedSearchQuery(e.target.value)}
                      placeholder="Search by request number..."
                      autoComplete="off"
                    />
                    {relatedSearching && (
                      <Text fontSize="sm" color="gray.500" mt={1}>Searching...</Text>
                    )}
                    {relatedSearchResults.length > 0 && (
                      <Box
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        mt={1}
                        bg="white"
                        borderWidth="1px"
                        borderRadius="md"
                        shadow="lg"
                        zIndex={10}
                        maxH="200px"
                        overflowY="auto"
                      >
                        {relatedSearchResults.map((t) => (
                          <Box
                            key={t.id}
                            px={3}
                            py={2}
                            cursor="pointer"
                            _hover={{ bg: 'gray.100' }}
                            onClick={() => selectRelatedTicket(t)}
                          >
                            <Text fontWeight="medium">{t.request_number}</Text>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>
              <FormErrorMessage>{errors.parent_ticket_id?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>
        )}

        {!requestorMode && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Assign to</FormLabel>
            <FormControl isInvalid={!!errors.assigned_to?.length}>
              <Box position="relative">
                {assignToDisplay ? (
                  <HStack>
                    <Input value={assignToDisplay} isReadOnly bg="gray.50" />
                    <Button type="button" size="sm" variant="outline" onClick={clearAssignTo}>
                      Clear
                    </Button>
                  </HStack>
                ) : (
                  <>
                    <Input
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      onFocus={() => {
                        if (userSearchQuery.trim().length >= 2 && userSearchResults.length === 0) {
                          searchUsers(userSearchQuery)
                        }
                      }}
                      placeholder="Search by login, email, first or last name (min 2 characters)..."
                      autoComplete="off"
                    />
                    {userSearching && (
                      <Box position="absolute" right={3} top="50%" transform="translateY(-50%)">
                        <Spinner size="sm" />
                      </Box>
                    )}
                    {userSearchResults.length > 0 && !userSearching && (
                      <Box
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        mt={1}
                        bg="white"
                        borderWidth="1px"
                        borderRadius="md"
                        shadow="lg"
                        zIndex={10}
                        maxH="240px"
                        overflowY="auto"
                      >
                        {userSearchResults.map((user) => (
                          <Box
                            key={user.id}
                            px={3}
                            py={2}
                            cursor="pointer"
                            _hover={{ bg: 'gray.100' }}
                            onClick={() => selectAssignUser(user)}
                            borderBottomWidth="1px"
                            borderColor="gray.100"
                            _last={{ borderBottom: 'none' }}
                          >
                            <Text fontWeight="medium" fontSize="sm">
                              {userLabel(user)}
                            </Text>
                            {user.user_email && (
                              <Text fontSize="xs" color="gray.500" mt={0.5}>
                                {user.user_email}
                              </Text>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>
              <FormHelperText fontSize="xs" color="gray.500" mt={1} mb={0}>
                Assign ticket to a user (search by login, email, first or last name).
              </FormHelperText>
              {currentUser && (
                <Checkbox
                  mt={3}
                  isChecked={assignToMe}
                  onChange={handleAssignToMeChange}
                  colorScheme="blue"
                  size="sm"
                >
                  <Text as="span" fontSize="sm">Assign to me</Text>
                </Checkbox>
              )}
              <FormErrorMessage>{errors.assigned_to?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>
        )}

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && <Button variant="outline" onClick={onCancel} isDisabled={saving}>Cancel</Button>}
          <Button type="submit" colorScheme="blue" isLoading={saving} loadingText={ticketRequestId ? 'Saving...' : 'Creating...'}>
            {ticketRequestId ? 'Save' : 'Create Ticket'}
          </Button>
        </HStack>
      </VStack>

      <Modal isOpen={isZoomOpen} onClose={closeZoom} size="full">
        <ModalOverlay />
        <ModalContent bg="blackAlpha.900">
          <ModalCloseButton color="white" zIndex={2} />
          <ModalBody display="flex" alignItems="center" justifyContent="center" py={12}>
            {(zoomAttachment?.preview?.startsWith('blob:') || (zoomAttachment?.isExisting && zoomAttachment?.savedMeta?.file_url)) ? (
              <Image
                src={zoomAttachment.preview?.startsWith('blob:') ? zoomAttachment.preview : zoomAttachment?.savedMeta?.file_url ?? ''}
                alt={zoomAttachment?.savedMeta?.name ?? zoomAttachment?.file.name}
                maxH="90vh"
                maxW="100%"
                objectFit="contain"
                onClick={closeZoom}
                cursor="pointer"
              />
            ) : (
              <Text color="white">Preview not available for this file: {zoomAttachment?.savedMeta?.name ?? zoomAttachment?.file.name}</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  )
}
